import {Inject, Injectable} from '@angular/core';
import {SharedConfig} from 'shared/lib/shared.config.interface';
import {BehaviorSubject, from, Observable} from 'rxjs';
import { set, get, omit } from 'lodash-es';
import {SharedConfigToken} from 'shared/lib/shared.config.token';
import {environment} from '../../../../src/environments/environment';
import {tryParseJson} from 'shared/_functions/tryParseJson';

@Injectable({
	providedIn: 'root'
})
export class LocalStorageService {
	private store$ = new BehaviorSubject(null);
	private rootKey = this.config.storagePrefix;
	private prefix = environment.storagePrefix;
	private storage = window.localStorage;

	private parse = tryParseJson;

	constructor(@Inject(SharedConfigToken) private config: SharedConfig) {
		this.checkOrInit();
	}

	private getItem = async (key: string) => this.storage.getItem(key);
	private setItem = async (key: string, value: any) => this.storage.setItem(key, JSON.stringify(value));
	private removeItem = (key: string) => {
		const snapshot = this.selectSnapshot(key);
		if (!snapshot) return;
		const storeValue = this.store$.getValue();
		const newValue = omit(storeValue, key);
		this.storage.removeItem(key);
		this.store$.next(newValue);
	}

	private getNewValue(path: string, value: any) {
		const current = this.store$.getValue();
		return set(Object.assign({}, current), path, value);
	}

	private async upsert(path: string, value: any) {
		const newValue = this.getNewValue(path, value);
		await this.setItem(this.rootKey, newValue);
		this.store$.next(newValue);
		return this.store$.getValue();
	}

	private upsertInstant(path: string, value: string) {
		const newValue = this.getNewValue(path, value);
		this.setItem(this.rootKey, newValue).then(() => {
			this.store$.next(newValue);
		});
	}

	private async select(path: string) {
		const current = this.store$.getValue();
		return get(current, path);
	}

	private fallbackGet(path?: string) {
		const root = this.storage.getItem(this.rootKey);
		if (!root) return;
		const tree = this.parse(root);
		return path ? get(tree, path) : tree;
	}

	private selectSnapshot(path: string) {
		const current = this.store$.getValue() || this.fallbackGet();
		return get(current, path);
	}

	private async checkOrInit() {
		const root = await this.getItem(this.rootKey);
		const initial = {};

		!root && await this.setItem(this.rootKey, initial);
		this.store$.next(root ?  this.parse(root) : initial);
	}

	private oldStorage(): [string, any][] {
		const keys = Object.keys(this.storage).filter(k => k.match(this.prefix));
		if (!keys.length) return [];
		return keys.map(k => [k, this.parse(this.storage.getItem(k))]);
	}

	set = (path: string, value: any): Observable<any> => from(this.upsert(path, value));
	get = (path: string): Observable<any> => from(this.select(path));
	remove = (path: string) => this.removeItem(path);
	setInstant = (path: string, value: any) => this.upsertInstant(path, value);
	getSnapshot = (path: string) => this.selectSnapshot(path);
	getOldValues = () => this.oldStorage();
}
